import React, { useState, useEffect } from 'react';
import { Database, Download, Trash2, Plus, HardDrive, Clock, RefreshCw, AlertTriangle } from 'lucide-react';
import { API_URL, openProtectedFile } from '../../services/api';

function BackupManager() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadBackups();
  }, []);

  const loadBackups = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/backup/`);
      const data = await res.json();
      setBackups(data.backups || []);
    } catch (err) {
      console.error('Erreur chargement backups:', err);
      showMessage('Erreur lors du chargement des sauvegardes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    try {
      setCreating(true);
      setMessage(null);
      const res = await fetch(`${API_URL}/backup/create`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        showMessage(`Sauvegarde "${data.filename}" creee avec succes (${data.size_mb} MB)`, 'success');
        loadBackups();
      } else {
        showMessage(data.error || 'Erreur lors de la creation', 'error');
      }
    } catch (err) {
      console.error('Erreur creation backup:', err);
      showMessage('Erreur de connexion au serveur', 'error');
    } finally {
      setCreating(false);
    }
  };

  const downloadBackup = async (filename) => {
    try {
      await openProtectedFile(`${API_URL}/backup/download/${filename}`, { download: true, filename });
    } catch (e) {
      showMessage(e.message || 'Erreur lors du téléchargement', 'error');
    }
  };

  const deleteBackup = async (filename) => {
    try {
      const res = await fetch(`${API_URL}/backup/${filename}`, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok) {
        showMessage(`Sauvegarde "${filename}" supprimee`, 'success');
        loadBackups();
      } else {
        showMessage(data.error || 'Erreur lors de la suppression', 'error');
      }
    } catch (err) {
      console.error('Erreur suppression backup:', err);
      showMessage('Erreur de connexion au serveur', 'error');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const totalSize = backups.reduce((sum, b) => sum + (b.size || 0), 0);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Database className="w-8 h-8 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Sauvegardes</h1>
            <p className="text-gray-500 text-sm">Gestion des sauvegardes de la base de donnees</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadBackups}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 transition text-gray-700"
          >
            <RefreshCw className="w-4 h-4" /> Actualiser
          </button>
          <button
            onClick={createBackup}
            disabled={creating}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition"
          >
            {creating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" /> Creation en cours...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" /> Creer une sauvegarde
              </>
            )}
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500"><HardDrive className="w-4 h-4" /> Total sauvegardes</div>
          <p className="text-2xl font-bold text-gray-800 mt-1">{backups.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500"><Database className="w-4 h-4" /> Espace utilise</div>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{formatSize(totalSize)}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500"><Clock className="w-4 h-4" /> Derniere sauvegarde</div>
          <p className="text-lg font-semibold text-gray-800 mt-1">
            {backups.length > 0 ? backups[0].date : 'Aucune'}
          </p>
        </div>
      </div>

      {/* Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-700">
          <p className="font-medium mb-1">Importance des sauvegardes</p>
          <p>
            Les sauvegardes contiennent l'integralite de votre base de donnees (clients, bijoux, ventes, stock...).
            Il est recommande de creer une sauvegarde quotidienne et de telecharger regulierement
            une copie sur un support externe (cle USB, cloud).
          </p>
        </div>
      </div>

      {/* Liste des backups */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
            <span className="ml-2 text-gray-500">Chargement...</span>
          </div>
        ) : backups.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Database className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucune sauvegarde disponible</p>
            <p className="text-sm mt-1">Cliquez sur "Creer une sauvegarde" pour commencer</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Nom du fichier</th>
                <th className="text-left px-4 py-3 font-medium">Taille</th>
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {backups.map((backup, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-emerald-500" />
                      <span className="font-mono text-gray-700">{backup.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{formatSize(backup.size)}</td>
                  <td className="px-4 py-3 text-gray-600">{backup.date}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => downloadBackup(backup.name)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition text-xs font-medium"
                      >
                        <Download className="w-3.5 h-3.5" /> Telecharger
                      </button>
                      {deleteConfirm === backup.name ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => deleteBackup(backup.name)}
                            className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-xs font-medium"
                          >
                            Confirmer
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition text-xs font-medium"
                          >
                            Annuler
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(backup.name)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition text-xs font-medium"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Supprimer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default BackupManager;
